import { apiURL } from './constants';
import axios from 'axios';

const baseURL = `${apiURL}/api/tracking_event_type`;

function getAuthHeaders() {
  axios.defaults.headers.common['Authorization'] = `Bearer ${localStorage.getItem('access_token')}`;
}

/**
 * Global axios response interceptor returns `error.response` (resolves the promise) instead of
 * rejecting. Callers must treat status >= 400 as failure or UI will show false success.
 */
function throwIfErrorResponse(response) {
  if (response && typeof response.status === 'number' && response.status >= 400) {
    const err = new Error(`HTTP ${response.status}`);
    err.response = response;
    throw err;
  }
  return response;
}

/**
 * Paginated list of ShipmentTrackingEventType rows (code, name, name_ar, sort_order).
 * Permission: shipment_tracking_event_types_list
 * @param {Object} params - page, page_size, pagination, search, code (exact), ordering (id|code|name|sort_order, prefix - for desc)
 */
export async function listTrackingEventTypes(params = {}) {
  getAuthHeaders();
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value);
    }
  });
  const qs = searchParams.toString();
  const url = `${baseURL}/${qs ? `?${qs}` : ''}`;
  const response = await axios.get(url, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  }).catch((error) => {
    console.error('listTrackingEventTypes error:', error);
    throw error;
  });
  return throwIfErrorResponse(response);
}

/**
 * Retrieve one event type by id.
 * Permission: shipment_tracking_event_types_view
 */
export async function getTrackingEventType(id) {
  getAuthHeaders();
  const response = await axios.get(`${baseURL}/${id}/`, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  }).catch((error) => {
    console.error('getTrackingEventType error:', error);
    throw error;
  });
  return throwIfErrorResponse(response);
}

/**
 * Create event type.
 * Permission: shipment_tracking_event_types_create
 * Body: code (required), name (required), name_ar (optional), sort_order (optional)
 */
export async function createTrackingEventType(body) {
  getAuthHeaders();
  const response = await axios.post(`${baseURL}/`, body, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  }).catch((error) => {
    console.error('createTrackingEventType error:', error);
    throw error;
  });
  return throwIfErrorResponse(response);
}

/**
 * Full or partial update.
 * Permission: shipment_tracking_event_types_update
 */
export async function updateTrackingEventType(id, body, partial = true) {
  getAuthHeaders();
  const method = partial ? 'patch' : 'put';
  const response = await axios[method](`${baseURL}/${id}/`, body, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  }).catch((error) => {
    console.error('updateTrackingEventType error:', error);
    throw error;
  });
  return throwIfErrorResponse(response);
}

/**
 * Delete (204). May return 400 if type is still referenced.
 * Permission: shipment_tracking_event_types_delete
 */
export async function deleteTrackingEventType(id) {
  getAuthHeaders();
  const response = await axios.delete(`${baseURL}/${id}/`, {
    withCredentials: true,
  }).catch((error) => {
    console.error('deleteTrackingEventType error:', error);
    throw error;
  });
  return throwIfErrorResponse(response);
}
